/**
 * MorningReport subscription section for the Settings page: status, and a way
 * to subscribe or manage the subscription (Stripe's hosted portal).
 */
import React, { useEffect, useState } from "react";
import { Alert, CircularProgress } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { format } from "date-fns";
import {
  SettingsRow,
  SettingsSection,
} from "components/dashboard/SettingsSection";
import {
  createCheckout,
  createPortal,
  getMorningReportAccess,
} from "../morningReportCallables";
import { markCheckoutStarted } from "../morningReportCheckout";
import { MORNING_REPORT_PATH } from "../morningReportPaths";

export default function MorningReportBillingSettings() {
  const [access, setAccess] = useState(null);
  const [loadError, setLoadError] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    getMorningReportAccess()
      .then((data) => !cancelled && setAccess(data))
      .catch(() => !cancelled && setLoadError(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const open = async (fn, payload, failure) => {
    setBusy(true);
    setError("");
    try {
      const { url } = await fn(payload);
      window.location.assign(url);
    } catch (err) {
      setError(err?.message || failure);
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <SettingsSection title="MorningReport">
        <Alert severity="error">
          Could not load your MorningReport subscription.
        </Alert>
      </SettingsSection>
    );
  }
  if (!access) {
    return (
      <SettingsSection title="MorningReport">
        <CircularProgress size={24} />
      </SettingsSection>
    );
  }

  const settingsUrl = window.location.href.split("?")[0];
  const caseListUrl = `${window.location.origin}${MORNING_REPORT_PATH}`;
  const subscribed = Boolean(access.subscribed);
  const periodEnd = access.currentPeriodEnd
    ? format(access.currentPeriodEnd, "MMMM d, yyyy")
    : null;
  const detail = subscribed
    ? access.cancelAtPeriodEnd && periodEnd
      ? `Your subscription is cancelled. You can open every case until ${periodEnd}.`
      : periodEnd
        ? `Your subscription is active at $9 a month and unlocks every case. Your next charge is on ${periodEnd}.`
        : "Your subscription is active at $9 a month and unlocks every case."
    : "Today's case is free. Subscribe to play past cases.";

  return (
    <SettingsSection title="MorningReport">
      {error ? (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError("")}>
          {error}
        </Alert>
      ) : null}
      <SettingsRow
        label="Subscription"
        detail={detail}
        action={
          subscribed ? (
            <LoadingButton
              variant="outlined"
              size="small"
              loading={busy}
              onClick={() =>
                open(
                  createPortal,
                  { returnUrl: settingsUrl },
                  "Could not open subscription settings.",
                )
              }
            >
              Manage
            </LoadingButton>
          ) : (
            <LoadingButton
              variant="outlined"
              size="small"
              loading={busy}
              onClick={() => {
                markCheckoutStarted();
                return open(
                  createCheckout,
                  {
                    successUrl: `${caseListUrl}?checkout=success&session_id={CHECKOUT_SESSION_ID}`,
                    cancelUrl: settingsUrl,
                  },
                  "Could not open checkout.",
                );
              }}
            >
              Subscribe for $9/month
            </LoadingButton>
          )
        }
      />
    </SettingsSection>
  );
}
